import { Account } from '@/domain/usecases'
import sentryConfig, { isIgnoredError } from '@/main/config/sentry-config'
import { settings } from '@/main/config/settings'
import { Router as RemixRouter } from '@remix-run/router'
import * as Sentry from '@sentry/react'
import { HydrationState } from '@sentry/react/types/types'
import { RouteObject } from 'react-router-dom'

interface DOMRouterOpts {
  basename?: string
  hydrationData?: HydrationState
  window?: Window
}

class ErrorMonitor {
  private static environments = ['prod', 'test']

  private _initialized: boolean = false

  public get initialized() {
    return this._initialized
  }

  public startMonitoring(): void {
    if (!ErrorMonitor.environments.includes(settings.APP_ENV)) {
      return
    }

    Sentry.init(sentryConfig)
    this._initialized = true
  }

  public identifyUser = (accountProfile: Account.Model | null) => {
    if (!accountProfile) return
    Sentry.setUser({
      id: accountProfile.id,
      accountType: accountProfile.accountType?.name,
      onboardingCompleted: accountProfile.onboardingCompleted,
      passPlanId: accountProfile.passPlan?.id,
      companyId: accountProfile.company.uniqueId,
      companyName: accountProfile.company.name
    })
  }

  public wrapCreateBrowserRouter = (
    createBrowserRouter: (routes: RouteObject[], opts?: DOMRouterOpts) => RemixRouter
  ) => {
    return Sentry.wrapCreateBrowserRouter(createBrowserRouter)
  }

  public captureException = (error: Error | unknown) => {
    if (isIgnoredError(error)) {
      return
    }

    if (error instanceof Error) {
      Sentry.captureException(error)
      return
    }

    if (this.isSerializedAppError(error)) {
      const reconstructedError = new Error(error.message)
      reconstructedError.name = error.name
      Sentry.captureException(reconstructedError)
      return
    }

    Sentry.captureException(error)
  }

  private isSerializedAppError = (
    error: unknown
  ): error is { customError: boolean; message: string; name: string } => {
    return (
      typeof error === 'object' &&
      error !== null &&
      'customError' in error &&
      'message' in error &&
      'name' in error
    )
  }
}

export default new ErrorMonitor()
